import {hexToRgba, rgbaToString} from '@backstage/utils/color-helpers';
import {
  Box,
  Flex,
  AspectRatio,
  Container as ChakraContainer,
  Image,
} from '@chakra-ui/react';
import {ChakraProps} from '@chakra-ui/system';
import {css, SerializedStyles} from '@emotion/react';
import styled from '@emotion/styled';
import {motion} from 'framer-motion';
import type {ITextContainerProps, IContainerProps} from './Video.interfaces';
import React, {FC, forwardRef, PropsWithChildren, Ref} from 'react';
import {VideoPlaceholder as VPH} from './assets/VideoPlaceholder';

export const VideoModuleWrapper = styled(motion.div)`
  background: #000;
  position: relative;

  /**
   * Maintans the players size and aspect ratio when the player is in fixed/picture in picture mode
   */
  &:before {
    content: '';
    width: 1px;
    margin-left: -1px;
    float: left;
    height: 0;
    padding-top: calc(9 / 16 * 100%);
  }

  &:after {
    /* to clear float */
    content: '';
    display: table;
    clear: both;
  }

  .player-switch.fixed-player .bitmovinplayer-container {
    position: fixed;
    bottom: 20px;
    left: 20px;
    width: 30vw;
    min-width: 250px;
    height: auto;
    z-index: 2;
    transition: 0.3s;
  }
`;

export const VideoWrapper: FC<PropsWithChildren> = ({
  children,
}): JSX.Element => (
  <Flex
    pos="absolute"
    top={0}
    left={0}
    bottom={0}
    right={0}
    pointerEvents="none"
    zIndex={1}
    justifyContent="center"
    alignItems="center"
  >
    {children}
  </Flex>
);

export const RatioWrapper: FC<PropsWithChildren> = ({
  children,
}): JSX.Element => (
  <Box width="100%" paddingBottom="56.25%" pos="relative">
    {children}
  </Box>
);

export const WatermarkTextContainer: FC<
  PropsWithChildren<ITextContainerProps>
> = ({children, position, className}) => (
  <Box
    className={className}
    pos="absolute"
    top={`${position ? position.y : 10}%`}
    left={`${position ? position.x : 50}%`}
    transform="auto"
    translateX="-50%"
    translateY="-50%"
    opacity={0.5}
    fontSize="1vw"
  >
    {children}
  </Box>
);

const overlayCss = css`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  ? > * {
    pointer-events: all;
  }
`;
type OverlayProps = PropsWithChildren<Pick<ChakraProps, 'zIndex'>> & {
  className?: string;
};
export const Overlay: FC<OverlayProps> = ({children, zIndex, className}) => (
  <Box css={overlayCss} zIndex={zIndex} className={className}>
    {children}
  </Box>
);

interface IVideoStyleProps {
  hasRoundedBorder?: boolean;
  ref: Ref<HTMLDivElement>;
}

export const Video: FC<PropsWithChildren<IVideoStyleProps>> = forwardRef<
  HTMLDivElement,
  PropsWithChildren<IVideoStyleProps>
>(
  ({children, hasRoundedBorder}, ref): JSX.Element => (
    <Box
      ref={ref}
      height="100%"
      width="100%"
      pos="relative"
      borderRadius={hasRoundedBorder ? '8px' : 0}
    >
      {children}
    </Box>
  )
);

export const LiveBug: FC<PropsWithChildren> = ({children}): JSX.Element => (
  <Box height="1.125rem" pos="absolute" top={8} right={8}>
    {children}
  </Box>
);

const displayControl = (
  flag: boolean | undefined,
  toggleValues: {on: string; off: string} = {on: 'initial', off: 'none'}
): SerializedStyles => css`
  display: ${flag || flag === undefined ? toggleValues.on : toggleValues.off};
`;

const barStyle = (color?: string): SerializedStyles => css`
  background: none;

  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: calc(50% - 1px);
    width: 100%;
    height: 2px;
    background-color: ${color};
  }
`;

const timelineMarkerHeight = 14;
const timelineMarkerWidth = 3;

const playerStyles = ({
  controlFlags,
  colorAccent,
  colorSeekbarLine,
}: IContainerProps): SerializedStyles => css`
  /* Disable the pause animation to hide timedPlayout */
  keyframes {
    to {
      opacity: 1;
    }
    from {
      opacity: 1;
    }
  }

  .bitmovinplayer-poster {
    background-color: #000;
  }

  .bmpui-ui-buffering-overlay {
    /* these default toggle values are associated with bitmovin player's default player stylesheet for buffering-overlay component */
    ${(displayControl(controlFlags?.buffer), {on: 'table-cell', off: 'none'})}
  }

  .bmpui-ui-recommendation-overlay {
    ${displayControl(controlFlags?.replay)}
  }

  .bmpui-ui-playbacktogglebutton {
    ${displayControl(controlFlags?.playPause)}
  }

  .bmpui-ui-settingstogglebutton {
    ${displayControl(controlFlags?.settings)}
  }

  .bmpui-ui-titlebar {
    ${displayControl(controlFlags?.title)}
  }

  .bmpui-ui-audiotracksettingstogglebutton {
    ${displayControl(controlFlags?.audioTracks)}
  }

  .bmpui-ui-subtitlesettingstogglebutton {
    ${displayControl(controlFlags?.captions)}
  }

  .bmpui-ui-watermark {
    ${displayControl(controlFlags?.liveBug)}
  }

  .bmpui-ui-hugeplaybacktogglebutton {
    display: block;
    ${displayControl(controlFlags?.hugePlay)}
  }

  .bmpui-ui-hugeplaybacktogglebutton.bmpui-on {
    pointer-events: ${controlFlags?.allowPauseOnClick ? 'auto' : 'none'};
    cursor: ${controlFlags?.allowPauseOnClick ? 'pointer' : 'auto'};
  }

  .bmpui-controlbar-top {
    ${displayControl(controlFlags?.scrub)}
  }

  .bmpui-ui-uicontainer {
    z-index: 6;
  }

  .bmpui-ui-controlbar {
    &.bmpui-hidden {
      opacity: 0; /* if you want to force it to stay visible */
    }
  }

  .bmpui-ui-fullscreentogglebutton {
    ${displayControl(controlFlags?.fullscreen)}
  }

  .bmpui-ui-volumeslider {
    color: ${colorAccent};
    ${displayControl(controlFlags?.volume)}
  }

  .bmpui-seekbar {
    .bmpui-seekbar-seekposition {
      ${barStyle(colorAccent)}
    }
    .bmpui-seekbar-backdrop {
      ${barStyle(colorSeekbarLine)}
    }

    .bmpui-seekbar-bufferlevel {
      ${barStyle()}
    }

    .bmpui-seekbar-playbackposition {
      ${barStyle(colorAccent)}
    }

    .bmpui-seekbar-playbackposition-marker {
      background: ${rgbaToString(hexToRgba(colorAccent, 0.5))};
      border: ${`2px solid ${colorAccent}`};
    }
  }

  .bmpui-seekbar-markers > .bmpui-seekbar-marker {
    /* these !importants are needed to override internal bitmovin player css style cascades */
    height: ${timelineMarkerHeight}px !important;
    width: ${timelineMarkerWidth}px !important;
    background-color: ${colorAccent} !important;
    > img {
      height: auto !important;
      width: auto !important;
      max-height: 50px;
      max-width: 50px;
    }
  }
`;

export const Container: FC<
  PropsWithChildren<IContainerProps & Pick<HTMLDivElement, 'className'>>
> = ({
  children,
  controlFlags,
  colorSeekbarLine,
  colorAccent,
  className,
  'data-testid': dataTestId,
}) => (
  <Box
    data-testid={dataTestId}
    pos="relative"
    margin="0 auto"
    width="100%"
    maxH="100%"
    background="none"
    css={playerStyles({controlFlags, colorSeekbarLine, colorAccent})}
    className={className}
  >
    {children}
  </Box>
);

interface VideoPlaceholderProps {
  posterUrl?: string;
}
export const VideoPlaceholder: FC<VideoPlaceholderProps> = ({posterUrl}) => {
  return (
    <AspectRatio ratio={16 / 9} backgroundColor="#000">
      {posterUrl ? (
        <Image src={posterUrl} />
      ) : (
        <ChakraContainer>
          <VPH />
        </ChakraContainer>
      )}
    </AspectRatio>
  );
};
